import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../Redux/hooks";
import { RootState } from "../store";
import {
  addToWishlist,
  getWishlist,
  checkWishlist,
  updateWishlistItem,
  removeFromWishlist,
  clearWishlist,
  clearWishlistState,
} from "../Redux/ActivityApi/slice";
import {
  WishlistItem,
  WishlistPagination,
  WishlistQueryParams,
  AddToWishlistPayload,
} from "../Redux/ActivityApi/types";
import { useAuth } from "./AuthContext";

export interface WishlistContextType {
  wishlist: WishlistItem[];
  wishlistTotal: number;
  wishlistCount: number;
  pagination: WishlistPagination | null;
  wishlistCheckMap: Record<string, boolean>;
  loading: boolean;
  error: string | null;

  // Actions
  fetchWishlist: (params?: WishlistQueryParams) => Promise<any>;
  addItemToWishlist: (data: AddToWishlistPayload) => Promise<any>;
  removeItemFromWishlist: (productId: string, productVariantId?: string) => Promise<any>;
  updateItem: (productId: string, data: { productVariantId?: string; note?: string }) => Promise<any>;
  clearAllWishlist: () => Promise<any>;
  toggleWishlist: (
    productId: string,
    options?: { productVariantId?: string; note?: string; ssin?: string }
  ) => Promise<any>;
  isItemWishlisted: (productId: string, ssin?: string) => boolean;
  checkProductWishlistStatus: (productId: string, productVariantId?: string) => Promise<boolean>;
  clearError: () => void;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const {
    wishlist,
    wishlistTotal,
    wishlistPagination,
    wishlistCheckMap,
    wishlistLoading,
    wishlistError,
  } = useAppSelector((state: RootState) => state.activity);

  // Fetch initial wishlist on mount or when user auth changes
  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(getWishlist({ page: 1, limit: 50 }));
  }, [dispatch, isAuthenticated]);

  const wishlistCount = useMemo(() => {
    return wishlistTotal || wishlist?.length || 0;
  }, [wishlistTotal, wishlist]);

  const fetchWishlist = useCallback(
    async (params?: WishlistQueryParams) => {
      if (!isAuthenticated) return null;
      return dispatch(getWishlist(params)).unwrap();
    },
    [dispatch, isAuthenticated]
  );

  const addItemToWishlist = useCallback(
    async (data: AddToWishlistPayload) => {
      if (!isAuthenticated) {
        toast.info("Please sign in to add items to your wishlist.", {
          toastId: "wishlist-signin-required",
        });
        navigate("/SignIn");
        return null;
      }
      const res = await dispatch(addToWishlist(data)).unwrap();
      dispatch(getWishlist({ page: 1, limit: 50 }));
      return res;
    },
    [dispatch, isAuthenticated, navigate]
  );

  const removeItemFromWishlist = useCallback(
    async (productId: string, productVariantId?: string) => {
      const res = await dispatch(removeFromWishlist({ productId, productVariantId })).unwrap();
      dispatch(getWishlist({ page: 1, limit: 50 }));
      return res;
    },
    [dispatch]
  );

  const updateItem = useCallback(
    async (
      productId: string,
      data: { productVariantId?: string; note?: string }
    ) => {
      return dispatch(updateWishlistItem({ productId, ...data })).unwrap();
    },
    [dispatch]
  );

  const clearAllWishlist = useCallback(async () => {
    return dispatch(clearWishlist()).unwrap();
  }, [dispatch]);

  const isItemWishlisted = useCallback(
    (productId: string, ssin?: string): boolean => {
      if (!productId && !ssin) return false;
      if (productId && wishlistCheckMap[productId] === true) {
        return true;
      }
      if (ssin && wishlistCheckMap[ssin] === true) {
        return true;
      }
      if (
        productId &&
        wishlistCheckMap[productId] === false &&
        (!ssin || wishlistCheckMap[ssin] === false)
      ) {
        return false;
      }
      return (
        wishlist?.some((item) => {
          const matchId =
            productId &&
            (item._id === productId ||
              item.ssin === productId ||
              item.wishlistItemId === productId);
          const matchSsin = ssin && (item.ssin === ssin || item._id === ssin);
          return Boolean(matchId || matchSsin);
        }) ?? false
      );
    },
    [wishlistCheckMap, wishlist]
  );

  const checkProductWishlistStatus = useCallback(
    async (productId: string, productVariantId?: string): Promise<boolean> => {
      if (!productId) return false;
      if (wishlistCheckMap[productId] !== undefined) {
        return Boolean(wishlistCheckMap[productId]);
      }
      try {
        const res = await dispatch(
          checkWishlist({ productId, productVariantId })
        ).unwrap();
        return Boolean(res.isWishlisted);
      } catch {
        return false;
      }
    },
    [dispatch, wishlistCheckMap]
  );

  const toggleWishlist = useCallback(
    async (
      productId: string,
      options?: { productVariantId?: string; note?: string; ssin?: string }
    ) => {
      if (!productId && !options?.ssin) return;
      const effectiveId = productId || options?.ssin || "";
      const currentlyWishlisted = isItemWishlisted(effectiveId, options?.ssin);

      if (currentlyWishlisted) {
        const matchingItem = wishlist?.find(
          (item) =>
            (effectiveId &&
              (item._id === effectiveId ||
                item.ssin === effectiveId ||
                item.wishlistItemId === effectiveId)) ||
            (options?.ssin && item.ssin === options.ssin)
        );
        const removeId = matchingItem?._id || matchingItem?.ssin || effectiveId;
        return removeItemFromWishlist(removeId, options?.productVariantId);
      } else {
        return addItemToWishlist({
          productId: effectiveId,
          productVariantId: options?.productVariantId,
          note: options?.note,
        });
      }
    },
    [isItemWishlisted, removeItemFromWishlist, addItemToWishlist, wishlist]
  );

  const clearError = useCallback(() => {
    dispatch(clearWishlistState());
  }, [dispatch]);

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        wishlistTotal,
        wishlistCount,
        pagination: wishlistPagination,
        wishlistCheckMap,
        loading: wishlistLoading,
        error: wishlistError,
        fetchWishlist,
        addItemToWishlist,
        removeItemFromWishlist,
        updateItem,
        clearAllWishlist,
        toggleWishlist,
        isItemWishlisted,
        checkProductWishlistStatus,
        clearError,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};

export default WishlistContext;
